import { useSuspenseQuery } from "@apollo/client";
import {
  AccountCard,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CurrencyAmountUI,
  DateUI,
  Icon,
  PrimaryFullScreenSpinner,
  StatusUI,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@repo/ui";
import { NotFoundError } from "@repo/utils";
import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { Suspense } from "react";
import { PiBuildingOffice } from "react-icons/pi";
import { DetailRow } from "~/components/DetailRow/DetailRow";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { RelatedLedgerTransactions } from "~/components/RelatedLedgerTransactions";
import { TransactionMonitoringFindingCard } from "~/components/TransactionMonitoring/TransactionMonitoringFindingCard";
import { graphql } from "~/gql/client";
import { opsHead, useOpsTitle } from "~/head";

export const Route = createFileRoute("/__auth/deposits_/$depositId")({
  head: () => opsHead("Deposit"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

function View() {
  const { depositId } = useParams({ strict: false });
  if (!depositId) {
    throw new NotFoundError();
  }

  const { data } = useSuspenseQuery(
    graphql(`
      query AdminDepositPage($depositId: ID!) {
        deposit: node(id: $depositId) {
          id
          ... on Deposit {
            id
            shortRef
            ledgerTransactions {
              id
              ...RelatedLedgerTransactions
            }
            date
            amount {
              amount
              currency
            }
            organisation {
              id
              name
              squareLogoUrl
            }
            paymentReference
            description
            destinationAsset {
              id
              ...AssetCardFields
            }
            transactionMonitoringDetails {
              status
              blockingFindings {
                id
                ...TransactionMonitoringFindingCardFields
              }
            }
          }
        }
      }
    `),
    {
      variables: {
        depositId,
      },
    },
  );

  const deposit = data?.deposit;
  if (!deposit || deposit.__typename !== "Deposit") {
    throw new NotFoundError();
  }

  useOpsTitle(`Deposit #${deposit.shortRef}`);

  return (
    <AdminPageContainer>
      <AdminPageHeader
        title={`Deposit #${deposit.shortRef}`}
        subtitle={<div className="font-mono">{deposit.id}</div>}
      />
      <Tabs defaultValue="details">
        <TabsList size="sm" className="mb-6">
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="transactions">Transactions</TabsTrigger>
        </TabsList>
        <TabsContent value="details">
          <div className="flex w-2/3 flex-col">
            <Card>
              <CardHeader>
                <CardTitle>Details</CardTitle>
              </CardHeader>
              <CardContent>
                {deposit.organisation && (
                  <DetailRow
                    label="Organisation"
                    value={
                      <Link
                        to={`/organisations/${deposit.organisation.id}`}
                        className="flex flex-row items-center gap-2 text-primary-interactive hover:text-primary-interactive hover:underline"
                      >
                        <Icon
                          size="8"
                          imageUrl={
                            deposit.organisation?.squareLogoUrl ?? undefined
                          }
                          fallbackIcon={<PiBuildingOffice />}
                        />
                        <span>{deposit.organisation.name}</span>
                      </Link>
                    }
                  />
                )}
                {deposit.amount && (
                  <DetailRow
                    label="Amount"
                    value={
                      <CurrencyAmountUI
                        amount={deposit.amount?.amount}
                        currency={deposit.amount.currency}
                      />
                    }
                  />
                )}
                <DetailRow
                  label="Date"
                  value={<DateUI date={deposit.date} variant="DATE_TZ" />}
                />
                {deposit.destinationAsset && (
                  <DetailRow
                    label="To Asset"
                    value={
                      <AccountCard
                        account={deposit.destinationAsset}
                        subText="PROVIDER"
                      />
                    }
                  />
                )}
                <DetailRow
                  label="Reference"
                  value={
                    <span className="bg-secondary p-2 font-mono text-primary">
                      {deposit.paymentReference}
                    </span>
                  }
                />
                <DetailRow label="Description" value={deposit.description} />
                {deposit.transactionMonitoringDetails && (
                  <DetailRow
                    label="Transaction Monitoring"
                    value={
                      <div className="flex flex-col gap-2">
                        {deposit.transactionMonitoringDetails.status && (
                          <div>
                            <StatusUI
                              status={
                                deposit.transactionMonitoringDetails.status
                              }
                            />
                          </div>
                        )}
                        {deposit.transactionMonitoringDetails
                          .blockingFindings && (
                          <div className="flex flex-col gap-1">
                            {deposit.transactionMonitoringDetails.blockingFindings.map(
                              (finding) => (
                                <TransactionMonitoringFindingCard
                                  key={finding.id}
                                  finding={finding}
                                />
                              ),
                            )}
                          </div>
                        )}
                      </div>
                    }
                  />
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        <TabsContent value="transactions">
          <RelatedLedgerTransactions
            transactions={deposit.ledgerTransactions ?? []}
          />
        </TabsContent>
      </Tabs>
    </AdminPageContainer>
  );
}
